import React, { useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../hooks/useAuthContext';
import { useLogout } from '../hooks/useLogout';
import { mountTileAnnotator } from '../annotator/tileAnnotator';
import './Annotate.css';

// Identifies the running build by its hashed bundle name (main.<hash>.js). Null in dev.
const BUNDLE_RE = /static\/js\/main\.([a-f0-9]+)\.js/;
const runningBuild = () => {
    const s = [...document.scripts].map((el) => el.src).find((src) => BUNDLE_RE.test(src));
    return s ? s.match(BUNDLE_RE)[1] : null;
};
const VERSION_CHECK_MS = 60 * 1000;

// The tile annotator (annotator/tileAnnotator.js) inside the site's login. Photos are never uploaded:
// the annotator connects them from the local computer and they are matched to the server's list by name.
const Annotate = () => {
    const { user, expireSession } = useAuthContext();
    const { logout } = useLogout();
    const rootRef = useRef(null);
    const [build] = useState(runningBuild);
    const [latestBuild, setLatestBuild] = useState(null); // set when the server has a newer build
    const staleRef = useRef(false);

    // Browsers (iPad Safari especially, and home-screen web apps) can keep running an old copy of this page
    // after a deploy. Annotating on stale code risks saving bad masks, so editing stops until reloaded.
    useEffect(() => {
        if (!build) return undefined; // dev server: no hashed bundle to compare
        let cancelled = false;
        const check = async () => {
            try {
                const res = await fetch(`/?build-check=${Date.now()}`, { cache: 'no-store' });
                const m = (await res.text()).match(BUNDLE_RE);
                if (!cancelled && m && m[1] !== build) {
                    staleRef.current = true;
                    setLatestBuild(m[1]);
                }
            } catch {
                // offline or transient error: check again next time
            }
        };
        check();
        const id = setInterval(check, VERSION_CHECK_MS);
        const onVisible = () => { if (document.visibilityState === 'visible') check(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', check);
        return () => {
            cancelled = true;
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', check);
        };
    }, [build]);

    useEffect(() => mountTileAnnotator(rootRef.current, {
        token: user.token,
        onAuthExpired: expireSession, // the server rejected the login: back to the login page
        isStale: () => staleRef.current
    }), [user.token, expireSession]);

    const reloadToLatest = () => window.location.replace(`/?v=${latestBuild || Date.now()}`);

    return (
        <div className="annotate-page">
            <header className="annotate-header">
                <h1>
                    CALM tile annotator
                    {build && <span className="annotate-build">build {build.slice(0, 8)}</span>}
                </h1>
                <div className="annotate-header-actions">
                    <span className="annotate-user">{user.email || (user.googleUser && user.googleUser.email)}</span>
                    <button className="annotate-logout-btn" onClick={logout}>Logout</button>
                </div>
            </header>
            {latestBuild && (
                <button className="annotate-stale-banner" onClick={reloadToLatest}>
                    This page is out of date - editing and saving are disabled. Tap here to reload.
                </button>
            )}
            <div className="annotate-body" ref={rootRef} />
        </div>
    );
};

export default Annotate;
